import { useState, useCallback } from 'react';
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import { storage } from '../lib/firebase';

interface UseStorageResult {
  uploadFile: (file: File, path: string) => Promise<{ url: string; thumbnailUrl: string }>;
  deleteFile: (path: string) => Promise<void>;
  getFileUrl: (path: string) => Promise<string>;
  uploading: boolean;
  progress: number;
  error: Error | null;
}

// -- Compression helpers --

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(img.src);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error('Failed to load image'));
    };
    img.src = URL.createObjectURL(file);
  });
}

function resizeToBlob(
  img: HTMLImageElement,
  maxWidth: number,
  quality: number,
  mimeType: string
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const ratio = Math.min(1, maxWidth / img.width);
    const width = Math.round(img.width * ratio);
    const height = Math.round(img.height * ratio);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      reject(new Error('Canvas context unavailable'));
      return;
    }

    ctx.drawImage(img, 0, 0, width, height);

    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Canvas toBlob failed'));
        }
      },
      mimeType,
      quality
    );
  });
}

async function compressImage(
  file: File,
  maxWidth: number,
  quality: number
): Promise<Blob> {
  // SVG : pas de compression
  if (file.type === 'image/svg+xml') {
    return file;
  }

  const img = await loadImage(file);

  // Pas besoin de resize si deja plus petit
  if (img.width <= maxWidth) {
    // Quand meme recompresser pour la qualite
    return resizeToBlob(img, img.width, quality, file.type || 'image/jpeg');
  }

  return resizeToBlob(img, maxWidth, quality, file.type || 'image/jpeg');
}

// -- Hook --

export function useStorage(): UseStorageResult {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<Error | null>(null);

  const uploadBlob = useCallback(
    (blob: Blob, storagePath: string, contentType: string): Promise<string> => {
      return new Promise((resolve, reject) => {
        const storageRef = ref(storage, storagePath);
        const task = uploadBytesResumable(storageRef, blob, { contentType });

        task.on(
          'state_changed',
          (snapshot) => {
            const pct = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            setProgress(Math.round(pct));
          },
          (err) => reject(err),
          async () => {
            const url = await getDownloadURL(task.snapshot.ref);
            resolve(url);
          }
        );
      });
    },
    []
  );

  const uploadFile = useCallback(
    async (file: File, path: string): Promise<{ url: string; thumbnailUrl: string }> => {
      setUploading(true);
      setProgress(0);
      setError(null);

      try {
        // Compress main image (max 1200px, quality 80%)
        const mainBlob = await compressImage(file, 1200, 0.8);
        const url = await uploadBlob(mainBlob, path, file.type);

        // Generate and upload thumbnail (400px)
        const ext = path.lastIndexOf('.');
        const thumbPath =
          ext !== -1
            ? `${path.substring(0, ext)}_thumb${path.substring(ext)}`
            : `${path}_thumb`;

        const thumbBlob = await compressImage(file, 400, 0.7);
        const thumbnailUrl = await uploadBlob(thumbBlob, thumbPath, file.type);

        setUploading(false);
        setProgress(100);
        return { url, thumbnailUrl };
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        setError(e);
        setUploading(false);
        throw e;
      }
    },
    [uploadBlob]
  );

  const deleteFile = useCallback(async (path: string) => {
    try {
      const storageRef = ref(storage, path);
      await deleteObject(storageRef);
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      setError(e);
      throw e;
    }
  }, []);

  const getFileUrl = useCallback(async (path: string): Promise<string> => {
    try {
      const storageRef = ref(storage, path);
      return await getDownloadURL(storageRef);
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      setError(e);
      throw e;
    }
  }, []);

  return { uploadFile, deleteFile, getFileUrl, uploading, progress, error };
}
